import React, { useEffect, useReducer } from 'react';

// Components and Others
import PropTypes from 'prop-types';
import update from 'immutability-helper';
import { Form, Col } from 'react-bootstrap';
import Loading from 'components/widgets/Loading';
import InputNumber from 'components/widgets/InputNumber';
import DialogAppBar from 'components/widgets/modal/DialogAppBar';
import DialogActions from 'components/widgets/modal/DialogActions';

// Material UI
import { makeStyles } from '@material-ui/core/styles';
import 'react-datepicker/dist/react-datepicker.css';
import {
    DialogContent, Dialog, Grid,
} from '@material-ui/core';
import printJS from 'print-js';
import KeyStore from 'utils/KeyStore';
import StringUtils from 'lib/StringUtils';
import NumberUtils from 'lib/NumberUtils';
import ModalUtils from 'utils/ModalUtils';
import { FetchPolicy } from 'utils/enum/Core';
import { useLazyQuery, useMutation, useQuery } from '@apollo/client';
import OpportunityQuery from 'services/graphQL/query/crm/OpportunityQuery';
import OpportunityMutation from 'services/graphQL/mutate/crm/OpportunityMutation';

const useStyles = makeStyles((theme) => ({
    radioInfo: {
        color: theme.palette.info.main,
        padding: 4,
        marginLeft: 4,
        '&.Mui-checked': {
            color: theme.palette.info.main,
        },
    },
    dialog: {
        width: 892,
    },
    dialogContent: {
        padding: theme.spacing(3),
        position: 'relative',
    },
    marginBottom0: {
        marginBottom: 0,
    },
    radioGroup: {
        marginBottom: 16,
    },
    loading: {
        position: 'absolute',
        backgroundColor: 'rgba(255, 255, 255, 0.6)',
        top: 0,
        left: 0,
        zIndex: 3,
    },
}));
const ACTION_TYPE = {
    SET_RECORD: 'setRecord',
    SET_PREVIEW_PURCHASE: 'setPreviewPurchase',
    SET_CUSTOM_FIELD_PURCHASE: 'setCustomFieldPurchase',
};
const keyStore = new KeyStore();

function reducer(state, action) {
    const { type, payload } = action;

    switch (type) {
    case ACTION_TYPE.SET_VALUE:
        const result = update(state, {
            customFieldPurchase: {
                customField: {
                    data: {
                        [payload.field]: { $set: payload.value },
                    },
                },
            },
        });
        payload.calculateSalesTax(result.customFieldPurchase.customField.data);
        return result;
    case ACTION_TYPE.SET_CUSTOM_FIELD_PURCHASE:
        return update(state, {
            customFieldPurchase: { $set: payload },
        });
    case ACTION_TYPE.SET_PREVIEW_PURCHASE:
        return update(state, {
            customFieldPurchase: {
                customField: {
                    data: { $merge: payload },
                },
            },
        });
    default:
        return {};
    }
}
const initState = {
    customFieldPurchase: {},
};

const PurchaseWorksheetDialog = ({
    open, opportunityId, onClose, className, maxWidth, textPrimaryButton,
}) => {
    const classes = useStyles();
    const [state, dispatch] = useReducer(reducer, initState);
    const selectedLot = keyStore.getSelectedLot();
    const data = state?.customFieldPurchase?.customField?.data || {};

    const [printPurchase, { loading: loadingPrintPurchase }] = useMutation(OpportunityMutation.PRINT_PURCHASE_WORKSHEET_FORM);
    const {
        loading,
        data: dataPurchase,
        error,
    } = useQuery(OpportunityQuery.GET_CUSTOM_FIELD_PURCHASE_WORKSHEET, {
        variables: { opportunityId, lotId: selectedLot.lotId },
        notifyOnNetworkStatusChange: true,
        fetchPolicy: FetchPolicy.NO_CACHE,
    });

    const [getPreviewCustomFieldPurchase, { data: dataPreviewPurchase, loading: loadingPreviewPurchase }] = useLazyQuery(
        OpportunityQuery.GET_PREVIEW_CUSTOM_FIELD_PURCHASE_WORKSHEET,
        {
            onError: (errorMessage) => {
                ModalUtils.errorMessage(null, errorMessage);
            },
            fetchPolicy: FetchPolicy.NETWORK_ONLY,
        },
    );

    useEffect(() => {
        if (loadingPreviewPurchase) return;

        if (dataPreviewPurchase?.getPreviewCustomFieldPurchaseWorksheet?.customField?.data) {
            dispatch({
                type: ACTION_TYPE.SET_PREVIEW_PURCHASE,
                payload: dataPreviewPurchase.getPreviewCustomFieldPurchaseWorksheet.customField.data,
            });
        }
    }, [dataPreviewPurchase, loadingPreviewPurchase]);

    useEffect(() => {
        if (loading) return;

        if (dataPurchase?.getCustomFieldPurchaseWorksheet) {
            dispatch({
                type: ACTION_TYPE.SET_CUSTOM_FIELD_PURCHASE,
                payload: dataPurchase?.getCustomFieldPurchaseWorksheet,
            });
        }

        if (error) {
            ModalUtils.errorMessage(null, error);
        }
    }, [error, dataPurchase, loading]);

    const calculateSalesTax = async (result) => {
        getPreviewCustomFieldPurchase({
            variables: {
                opportunityId,
                lotId: selectedLot.lotId,
                record: {
                    salesPrice: result.salesPrice,
                    downPayment: result.downPayment,
                    tradeOffer: result.tradeOffer,
                    tradePayoff: result.tradePayoff,
                    dealerFee: result.dealerFee,
                    userField1: result.userField1,
                    userField2: result.userField2,
                },
            },
        });
    };

    const onChange = ({
        field, value,
    }) => dispatch({
        type: ACTION_TYPE.SET_VALUE,
        payload: {
            field,
            value: NumberUtils.toFloat(value),
            calculateSalesTax,
        },
    });

    const onPrintPurchase = async () => {
        try {
            const result = await printPurchase({
                variables: {
                    lotId: selectedLot.lotId,
                    opportunityId,
                    form: {
                        formId: state?.customFieldPurchase?.id,
                        dealerFee: data.dealerFee,
                        downPayment: data.downPayment,
                        salesPrice: data.salesPrice,
                        salesTax: data.salesTax,
                        taxable: data.taxable,
                        tradeOffer: data.tradeOffer,
                        tradePayoff: data.tradePayoff,
                        userField1: data.userField1,
                        userField2: data.userField2,
                    },
                },
            });

            if (!StringUtils.isEmpty(result.data?.printPurchaseWorksheetForm)) {
                printJS({
                    printable: result.data.printPurchaseWorksheetForm,
                    type: 'pdf',
                    showModal: false,
                    onLoadingEnd: onClose,
                });
            }
        } catch (e) {
            ModalUtils.errorMessage(e?.graphQLErrors);
        }
    };

    return (
        <Dialog
            open={open}
            fullWidth
            maxWidth={maxWidth}
            PaperProps={{
                className: classes.dialog,
            }}
            className={className}
            aria-labelledby="alert-dialog-title"
            aria-describedby="alert-dialog-description"
            onClick={(e) => e.stopPropagation()}
        >
            <DialogAppBar title="Purchase Worksheet" iconSize="sm" onClose={onClose} disabled={loading} />
            <DialogContent className={classes.dialogContent}>
                {(loading || loadingPreviewPurchase) && <Loading className={classes.loading} />}
                <Grid container spacing={6} className="am-form">
                    <Grid item xs={12}>
                        <Form.Group as={Col}>
                            <Form.Label>Sales Price:</Form.Label>
                            <InputNumber
                                thousandSeparator
                                value={data.salesPrice}
                                decimalScale={2}
                                onBlur={(e) => onChange({ value: e.target.value, field: 'salesPrice' })}
                            />
                        </Form.Group>
                        <Form.Group as={Col}>
                            <Form.Label>Down Payment:</Form.Label>
                            <InputNumber
                                thousandSeparator
                                value={data.downPayment}
                                decimalScale={2}
                                onBlur={(e) => onChange({ value: e.target.value, field: 'downPayment' })}
                            />
                        </Form.Group>
                        <Form.Group as={Col}>
                            <Form.Label>Trade Value:</Form.Label>
                            <InputNumber
                                thousandSeparator
                                value={data.tradeOffer}
                                decimalScale={2}
                                onBlur={(e) => onChange({ value: e.target.value, field: 'tradeOffer' })}
                            />
                        </Form.Group>
                        <Form.Group as={Col}>
                            <Form.Label>Trade Payoff:</Form.Label>
                            <InputNumber
                                thousandSeparator
                                value={data.tradePayoff}
                                decimalScale={2}
                                onBlur={(e) => onChange({ value: e.target.value, field: 'tradePayoff' })}
                            />
                        </Form.Group>
                        <Form.Group as={Col}>
                            <Form.Label>Dealer Fee:</Form.Label>
                            <InputNumber
                                thousandSeparator
                                value={data.dealerFee}
                                decimalScale={2}
                                disabled={!data.dealerFeeEditable}
                                onBlur={(e) => onChange({ value: e.target.value, field: 'dealerFee' })}
                            />
                        </Form.Group>
                        {!StringUtils.isEmpty(data.userField1Name) && (
                            <Form.Group as={Col}>
                                <Form.Label>{`${data.userField1Name}:`}</Form.Label>
                                <InputNumber
                                    thousandSeparator
                                    value={data.userField1}
                                    decimalScale={2}
                                    onBlur={(e) => onChange({ value: e.target.value, field: 'userField1' })}
                                />
                            </Form.Group>
                        )}
                        {!StringUtils.isEmpty(data.userField2Name) && (
                            <Form.Group as={Col}>
                                <Form.Label>{`${data.userField2Name}:`}</Form.Label>
                                <InputNumber
                                    thousandSeparator
                                    value={data.userField2}
                                    decimalScale={2}
                                    onBlur={(e) => onChange({ value: e.target.value, field: 'userField2' })}
                                />
                            </Form.Group>
                        )}
                        <Form.Group as={Col}>
                            <Form.Label>Sales Tax:</Form.Label>
                            <InputNumber
                                thousandSeparator
                                value={data.salesTax}
                                decimalScale={2}
                                disabled
                                onBlur={(e) => onChange({ value: e.target.value, field: 'salesTax' })}
                            />
                        </Form.Group>
                    </Grid>
                </Grid>
            </DialogContent>
            <DialogActions
                onClickSecondary={onClose}
                onClickPrimary={onPrintPurchase}
                titlePrimary={textPrimaryButton}
                disablePrimaryButton={loading || loadingPreviewPurchase || loadingPrintPurchase}
                disableSecondaryButton={loading}
            />
        </Dialog>
    );
};

PurchaseWorksheetDialog.propTypes = {
    open: PropTypes.bool.isRequired,
    opportunityId: PropTypes.string.isRequired,
    className: PropTypes.string,
    maxWidth: PropTypes.string,
    textPrimaryButton: PropTypes.string,
    onClose: PropTypes.func.isRequired,
};

PurchaseWorksheetDialog.defaultProps = {
    className: '',
    maxWidth: 'md',
    textPrimaryButton: 'Save',
};

export default PurchaseWorksheetDialog;
